"use client"

import { ChevronLeftIcon, ChevronRightIcon } from "@/components/ui/icons"
import { cn } from "@/lib/utils"

export interface CarouselNavProps {
  onPrev: () => void
  onNext: () => void
  /** Disable the prev button — typically when scroll is at the start. */
  atStart?: boolean
  /** Disable the next button — typically when scroll is at the end. */
  atEnd?: boolean
  /** Wrapper class — defaults to right-aligned. Pass `justify-center` etc. to override. */
  className?: string
  prevLabel?: string
  nextLabel?: string
}

/**
 * Canonical carousel stepper — two circular nav buttons (prev/next) for any
 * horizontally-scrolling content. Soft grey fill, lighter chevrons, edge
 * disabling. Pair with a scroll container that exposes `atStart` / `atEnd`
 * via a scroll listener (see `useCarouselEdges` for a ready-made hook).
 *
 * Visual spec (locked in via membership testimonials, 2026-04):
 *   - 40×40 circle, `bg-neutral-100` (neutral-100), hover `bg-neutral-200`,
 *     disabled `bg-neutral-50`
 *   - icon: DS chevron at 24×24, `stroke-width: 2` (heavier than DS default
 *     1.5 to read at small sizes), `text-neutral-400` → hover `text-neutral-500`
 *     → disabled `text-neutral-300`
 *   - icons nudged 1px outward for optical centering inside the circle
 *   - `cursor-pointer` active, `cursor-default` disabled (no `not-allowed`)
 */
export function CarouselNav({
  onPrev,
  onNext,
  atStart = false,
  atEnd = false,
  className,
  prevLabel = "Previous",
  nextLabel = "Next",
}: CarouselNavProps) {
  return (
    <div className={cn("flex justify-end gap-2", className)}>
      <button
        type="button"
        onClick={onPrev}
        disabled={atStart}
        aria-label={prevLabel}
        className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-neutral-100 text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-500 disabled:cursor-default disabled:bg-neutral-50 disabled:text-neutral-300 disabled:hover:bg-neutral-50 disabled:hover:text-neutral-300 [&_svg_path]:[stroke-width:2]"
      >
        <ChevronLeftIcon width={24} height={24} className="-translate-x-px" />
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={atEnd}
        aria-label={nextLabel}
        className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-neutral-100 text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-500 disabled:cursor-default disabled:bg-neutral-50 disabled:text-neutral-300 disabled:hover:bg-neutral-50 disabled:hover:text-neutral-300 [&_svg_path]:[stroke-width:2]"
      >
        <ChevronRightIcon width={24} height={24} className="translate-x-px" />
      </button>
    </div>
  )
}
